import { useContext, useEffect, useState } from 'react';
import BaseViewsContext from '../contexts/BaseViewsContext';
import isDefined from '../isDefined';
import _ from 'lodash';

// A field's business-language label (BaseView nameAlias), falling back to a
// start-cased version of the raw field. Split out from getDrillDownFieldName so
// callers holding a bare field — a chart's dimension, say — can resolve a label
// without inventing a FixedFilter to pass in.
export const getFieldLabel = ({
  field,
  dataset,
  baseViews,
}: {
  field: string;
  dataset?: string;
  baseViews: { [dataset: string]: FleetOps.BaseView | undefined };
}): string => {
  const upCasedField = _.startCase(field);

  const relevantViews = (() => {
    const baseViewsArr = Object.values(baseViews).filter(isDefined);
    if (!dataset) {
      return baseViewsArr;
    }

    return baseViewsArr.filter((baseView) => baseView.type === dataset);
  })();
  const relevantFields = relevantViews
    .map((v) => v.fields[field])
    .filter(isDefined)
    .filter((f) => f.nameAlias);
  if (relevantFields.length === 0) {
    return upCasedField;
  } else {
    const f = relevantFields[0];

    return f.nameAlias ? f.nameAlias : upCasedField;
  }
};

export const getDrillDownFieldName = (
  drillDown: { field: string; dataset?: string } | undefined,
  baseViews: { [dataset: string]: FleetOps.BaseView | undefined },
): string =>
  getFieldLabel({
    field: drillDown ? drillDown.field : '',
    dataset: drillDown ? drillDown.dataset : undefined,
    baseViews,
  });

const useDrillDownFieldName = (drillDown?: FixedFilter): string => {
  const { baseViews } = useContext(BaseViewsContext);

  const [upCasedField] = useState(
    _.startCase(drillDown ? drillDown.field : ''),
  );
  const [name, setName] = useState<string>(upCasedField);

  useEffect(() => {
    setName(getDrillDownFieldName(drillDown, baseViews));
  }, [baseViews, drillDown, upCasedField]);

  return name;
};

export default useDrillDownFieldName;
